import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Plus, Trash2, CalendarOff, Sparkles, CalendarHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CA_HOLIDAYS, upcomingHolidays } from "@/data/holidays";

const WEEKDAYS = [
    { v: 1, label: "Mon" },
    { v: 2, label: "Tue" },
    { v: 3, label: "Wed" },
    { v: 4, label: "Thu" },
    { v: 5, label: "Fri" },
    { v: 6, label: "Sat" },
    { v: 7, label: "Sun" },
];

const SERVICE_DURATION_LABELS = {
    free_consultation: "Free 20-min consultation",
    paid_consultation: "Paid relocation consultation",
    shortlisting: "Rental shortlisting",
    viewing_on_behalf: "Viewing on behalf",
    move_day_2h: "Move-day · 2h",
    move_day_4h: "Move-day · 4h",
    move_day_6h: "Move-day · 6h",
    move_day_8h: "Move-day · 8h",
    move_day_10h: "Move-day · 10h",
    packing_support: "Packing support",
    cleaning_support: "Cleaning support",
    vendor_coordination: "Vendor coordination",
    landlord_connection: "Landlord connection",
};

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

export default function AdminAvailability() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [s, setS] = useState(null);

    // Inputs for adding new entries
    const [newBlockedDate, setNewBlockedDate] = useState("");
    const [newHolidayDate, setNewHolidayDate] = useState("");
    const [newBlockedSlotDate, setNewBlockedSlotDate] = useState("");
    const [newBlockedSlotTime, setNewBlockedSlotTime] = useState("");

    useEffect(() => {
        load();
    }, []);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/availability");
            setS(data);
        } catch {
            toast.error("Failed to load availability");
        } finally {
            setLoading(false);
        }
    }

    function update(field, value) {
        setS((prev) => ({ ...prev, [field]: value }));
    }

    function toggleDay(day) {
        const cur = new Set(s.working_days);
        if (cur.has(day)) cur.delete(day);
        else cur.add(day);
        update("working_days", Array.from(cur).sort());
    }

    function addToList(field, value, setter) {
        const v = (value || "").trim();
        if (!v) return;
        if ((s[field] || []).includes(v)) {
            toast.info("Already added");
            return;
        }
        update(field, [...(s[field] || []), v].sort());
        setter("");
    }

    function removeFromList(field, value) {
        update(
            field,
            (s[field] || []).filter((x) => x !== value)
        );
    }

    function importCanadianHolidays() {
        const upcoming = upcomingHolidays();
        if (upcoming.length === 0) {
            toast.info("No upcoming holidays in the next 2 years to import.");
            return;
        }
        const existing = new Set(s.holiday_dates || []);
        const added = [];
        for (const h of upcoming) {
            if (!existing.has(h.date)) {
                existing.add(h.date);
                added.push(h);
            }
        }
        if (added.length === 0) {
            toast.info("All upcoming Canadian holidays are already in your list.");
            return;
        }
        update("holiday_dates", Array.from(existing).sort());
        toast.success(`Added ${added.length} Canadian / Ontario holidays. Don't forget to Save.`);
    }

    function addBlockedSlot() {
        const d = newBlockedSlotDate.trim();
        const t = newBlockedSlotTime.trim();
        if (!d || !t) {
            toast.info("Pick both a date and a slot time.");
            return;
        }
        const exists = (s.blocked_slots || []).some((x) => x.date === d && x.time === t);
        if (exists) {
            toast.info("Already blocked");
            return;
        }
        update("blocked_slots", [...(s.blocked_slots || []), { date: d, time: t }]);
        setNewBlockedSlotDate("");
        setNewBlockedSlotTime("");
    }

    function removeBlockedSlot(d, t) {
        update(
            "blocked_slots",
            (s.blocked_slots || []).filter((x) => !(x.date === d && x.time === t))
        );
    }

    async function save() {
        setSaving(true);
        try {
            await api.put("/admin/availability", s);
            toast.success("Availability saved");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    }

    const previewSlots = useMemo(() => {
        if (!s) return [];
        try {
            const [sh, sm] = s.work_start.split(":").map(Number);
            const [eh, em] = s.work_end.split(":").map(Number);
            const start = sh * 60 + sm;
            const end = eh * 60 + em;
            const dur = s.slot_duration_minutes || 30;
            if (end <= start || dur <= 0) return [];
            const out = [];
            for (let cur = start; cur + dur <= end; cur += dur) {
                const hh = Math.floor(cur / 60);
                const mm = cur % 60;
                const suf = hh < 12 ? "AM" : "PM";
                const hh12 = hh % 12 || 12;
                out.push(`${hh12}:${String(mm).padStart(2, "0")} ${suf}`);
            }
            return out;
        } catch {
            return [];
        }
    }, [s]);

    if (loading || !s) {
        return (
            <div className="flex h-40 items-center justify-center">
                <Loader2 className="animate-spin text-[#F26B1F]" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-availability">
            {/* HEADER + SAVE */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 className="font-display text-2xl font-semibold text-[#0E2A47]">
                        Set Availability
                    </h2>
                    <p className="text-sm text-[#475569]">
                        Control which days and times appear on the booking calendar.
                        Changes take effect immediately.
                    </p>
                </div>
                <Button
                    onClick={save}
                    disabled={saving}
                    className="rounded-full bg-[#F26B1F] text-white hover:bg-[#D95A15]"
                    data-testid="save-availability-btn"
                >
                    {saving ? (
                        <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                        <Save size={16} className="mr-2" />
                    )}
                    Save changes
                </Button>
            </div>

            {/* WORKING DAYS */}
            <section className="card-soft p-6">
                <h3 className="font-display text-lg font-semibold text-[#0E2A47]">
                    Working days
                </h3>
                <p className="mt-1 text-sm text-[#475569]">
                    Tick the days you're available. Unticked days are hidden from
                    visitors automatically.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {WEEKDAYS.map((d) => {
                        const active = s.working_days.includes(d.v);
                        return (
                            <button
                                key={d.v}
                                type="button"
                                onClick={() => toggleDay(d.v)}
                                data-testid={`day-${d.label.toLowerCase()}`}
                                className={`rounded-full border-2 px-5 py-2 text-sm font-semibold transition-all ${
                                    active
                                        ? "border-[#F26B1F] bg-[#FDF1E7] text-[#F26B1F]"
                                        : "border-[#ECE4D3] bg-white text-[#0E2A47]/60 hover:border-[#0E2A47]"
                                }`}
                            >
                                {d.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* WORKING HOURS + SLOT */}
            <section className="card-soft p-6">
                <h3 className="font-display text-lg font-semibold text-[#0E2A47]">
                    Working hours &amp; slot length
                </h3>
                <p className="mt-1 text-sm text-[#475569]">
                    Slots are generated automatically from these settings.
                </p>
                <div className="mt-5 grid gap-5 md:grid-cols-3">
                    <div>
                        <Label htmlFor="work_start">Start time</Label>
                        <Input
                            id="work_start"
                            type="time"
                            value={s.work_start}
                            onChange={(e) => update("work_start", e.target.value)}
                            className="mt-2 h-11 rounded-xl"
                            data-testid="input-work-start"
                        />
                    </div>
                    <div>
                        <Label htmlFor="work_end">End time</Label>
                        <Input
                            id="work_end"
                            type="time"
                            value={s.work_end}
                            onChange={(e) => update("work_end", e.target.value)}
                            className="mt-2 h-11 rounded-xl"
                            data-testid="input-work-end"
                        />
                    </div>
                    <div>
                        <Label htmlFor="slot_duration">Slot length (minutes)</Label>
                        <Input
                            id="slot_duration"
                            type="number"
                            min={5}
                            step={5}
                            value={s.slot_duration_minutes}
                            onChange={(e) => update("slot_duration_minutes", Number(e.target.value))}
                            className="mt-2 h-11 rounded-xl"
                            data-testid="input-slot-duration"
                        />
                    </div>
                </div>

                <div className="mt-5 grid gap-5 md:grid-cols-2">
                    <div>
                        <Label htmlFor="lead_time">Minimum lead time (hours)</Label>
                        <Input
                            id="lead_time"
                            type="number"
                            min={0}
                            value={s.lead_time_hours}
                            onChange={(e) => update("lead_time_hours", Number(e.target.value))}
                            className="mt-2 h-11 rounded-xl"
                            data-testid="input-lead-time"
                        />
                        <p className="mt-1 text-xs text-[#475569]">
                            Slots within this window from now are auto-hidden.
                        </p>
                    </div>
                    <div>
                        <Label htmlFor="max_advance">Max advance booking (days)</Label>
                        <Input
                            id="max_advance"
                            type="number"
                            min={1}
                            value={s.max_advance_days}
                            onChange={(e) => update("max_advance_days", Number(e.target.value))}
                            className="mt-2 h-11 rounded-xl"
                            data-testid="input-max-advance"
                        />
                    </div>
                </div>

                {/* Preview */}
                <div className="mt-6 rounded-2xl bg-[#FAF6EE] p-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#0E2A47]/60">
                        <Sparkles className="mr-1 inline" size={12} /> Preview · slots per
                        day
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {previewSlots.length === 0 && (
                            <span className="text-sm text-[#475569]">
                                Invalid window — check start/end times.
                            </span>
                        )}
                        {previewSlots.map((t) => (
                            <span
                                key={t}
                                className="rounded-full bg-white px-3 py-1 text-xs font-medium text-[#0E2A47]"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* BLOCKED DATES */}
            <div className="grid gap-6 md:grid-cols-2">
                <section className="card-soft p-6">
                    <h3 className="font-display text-lg font-semibold text-[#0E2A47]">
                        Blocked dates
                    </h3>
                    <p className="mt-1 text-sm text-[#475569]">
                        Specific days you're not available (vacation, sick, etc.).
                    </p>
                    <div className="mt-4 flex gap-2">
                        <Input
                            type="date"
                            min={todayIso()}
                            value={newBlockedDate}
                            onChange={(e) => setNewBlockedDate(e.target.value)}
                            className="h-11 rounded-xl"
                            data-testid="input-blocked-date"
                        />
                        <Button
                            onClick={() =>
                                addToList("blocked_dates", newBlockedDate, setNewBlockedDate)
                            }
                            className="h-11 shrink-0 rounded-xl bg-[#0E2A47] text-white hover:bg-[#0a1f36]"
                            data-testid="add-blocked-date-btn"
                        >
                            <Plus size={16} className="mr-1" /> Add
                        </Button>
                    </div>
                    <ListChips
                        items={s.blocked_dates}
                        onRemove={(v) => removeFromList("blocked_dates", v)}
                        emptyLabel="No blocked dates."
                        testid="blocked-dates-list"
                    />
                </section>

                <section className="card-soft p-6">
                    <h3 className="font-display text-lg font-semibold text-[#0E2A47]">
                        Holidays
                    </h3>
                    <p className="mt-1 text-sm text-[#475569]">
                        Public holidays / closures — same effect as blocked dates, but
                        labelled separately for clarity.
                    </p>
                    <Button
                        type="button"
                        onClick={importCanadianHolidays}
                        variant="outline"
                        className="mt-3 h-9 rounded-full border-dashed border-[#F26B1F] text-xs text-[#F26B1F] hover:bg-[#FDF1E7]"
                        data-testid="import-ca-holidays-btn"
                    >
                        <CalendarHeart size={14} className="mr-1.5" /> Import Canadian /
                        Ontario holidays
                    </Button>
                    <div className="mt-4 flex gap-2">
                        <Input
                            type="date"
                            min={todayIso()}
                            value={newHolidayDate}
                            onChange={(e) => setNewHolidayDate(e.target.value)}
                            className="h-11 rounded-xl"
                            data-testid="input-holiday-date"
                        />
                        <Button
                            onClick={() =>
                                addToList("holiday_dates", newHolidayDate, setNewHolidayDate)
                            }
                            className="h-11 shrink-0 rounded-xl bg-[#0E2A47] text-white hover:bg-[#0a1f36]"
                            data-testid="add-holiday-date-btn"
                        >
                            <Plus size={16} className="mr-1" /> Add
                        </Button>
                    </div>
                    <HolidayList
                        items={s.holiday_dates}
                        onRemove={(v) => removeFromList("holiday_dates", v)}
                        emptyLabel="No holidays added."
                        testid="holiday-dates-list"
                    />
                </section>
            </div>

            {/* BLOCKED SLOTS */}
            <section className="card-soft p-6">
                <h3 className="font-display text-lg font-semibold text-[#0E2A47]">
                    <CalendarOff size={18} className="mr-2 inline text-[#F26B1F]" />
                    Block specific time slots
                </h3>
                <p className="mt-1 text-sm text-[#475569]">
                    For days that are mostly open but you have a specific slot taken
                    (e.g. dentist appointment).
                </p>
                <div className="mt-4 grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                    <Input
                        type="date"
                        min={todayIso()}
                        value={newBlockedSlotDate}
                        onChange={(e) => setNewBlockedSlotDate(e.target.value)}
                        className="h-11 rounded-xl"
                        data-testid="input-blocked-slot-date"
                    />
                    <select
                        value={newBlockedSlotTime}
                        onChange={(e) => setNewBlockedSlotTime(e.target.value)}
                        className="h-11 rounded-xl border border-[#ECE4D3] bg-white px-3 text-sm"
                        data-testid="input-blocked-slot-time"
                    >
                        <option value="">— Pick a time —</option>
                        {previewSlots.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                    <Button
                        onClick={addBlockedSlot}
                        className="h-11 rounded-xl bg-[#0E2A47] text-white hover:bg-[#0a1f36]"
                        data-testid="add-blocked-slot-btn"
                    >
                        <Plus size={16} className="mr-1" /> Block slot
                    </Button>
                </div>
                <div className="mt-4 space-y-2" data-testid="blocked-slots-list">
                    {(s.blocked_slots || []).length === 0 && (
                        <p className="text-sm text-[#475569]">No specific slots blocked.</p>
                    )}
                    {(s.blocked_slots || []).map((b) => (
                        <div
                            key={`${b.date}-${b.time}`}
                            className="flex items-center justify-between rounded-xl border border-[#ECE4D3] bg-white px-4 py-2.5 text-sm"
                        >
                            <span className="font-medium text-[#0E2A47]">
                                {b.date} · {b.time}
                            </span>
                            <button
                                type="button"
                                onClick={() => removeBlockedSlot(b.date, b.time)}
                                className="rounded-full p-1.5 text-rose-500 hover:bg-rose-50"
                                aria-label="Remove"
                                data-testid={`remove-slot-${b.date}-${b.time}`}
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICE DURATIONS */}
            <section className="card-soft p-6">
                <h3 className="font-display text-lg font-semibold text-[#0E2A47]">
                    Per-service duration (minutes)
                </h3>
                <p className="mt-1 text-sm text-[#475569]">
                    How long each consultation/service appointment typically takes. Shown
                    to clients on the booking page.
                </p>
                <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(SERVICE_DURATION_LABELS).map(([key, label]) => (
                        <div key={key}>
                            <Label htmlFor={`dur-${key}`} className="text-sm">
                                {label}
                            </Label>
                            <Input
                                id={`dur-${key}`}
                                type="number"
                                min={5}
                                step={5}
                                value={s.service_durations?.[key] ?? 30}
                                onChange={(e) =>
                                    update("service_durations", {
                                        ...(s.service_durations || {}),
                                        [key]: Number(e.target.value),
                                    })
                                }
                                className="mt-2 h-11 rounded-xl"
                                data-testid={`dur-${key}`}
                            />
                        </div>
                    ))}
                </div>
            </section>

            <div className="sticky bottom-4 z-10 flex justify-end">
                <Button
                    onClick={save}
                    disabled={saving}
                    className="rounded-full bg-[#F26B1F] px-6 text-white shadow-[0_10px_30px_rgba(242,107,31,0.35)] hover:bg-[#D95A15]"
                    data-testid="save-availability-btn-bottom"
                >
                    {saving ? (
                        <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                        <Save size={16} className="mr-2" />
                    )}
                    Save changes
                </Button>
            </div>
        </div>
    );
}

function ListChips({ items, onRemove, emptyLabel, testid }) {
    if (!items || items.length === 0) {
        return <p className="mt-4 text-sm text-[#475569]">{emptyLabel}</p>;
    }
    return (
        <div className="mt-4 flex flex-wrap gap-2" data-testid={testid}>
            {items.map((v) => (
                <span
                    key={v}
                    className="inline-flex items-center gap-2 rounded-full border border-[#ECE4D3] bg-white px-3 py-1.5 text-xs font-medium text-[#0E2A47]"
                >
                    {v}
                    <button
                        type="button"
                        onClick={() => onRemove(v)}
                        className="rounded-full p-0.5 text-rose-500 hover:bg-rose-50"
                        aria-label="Remove"
                    >
                        <Trash2 size={12} />
                    </button>
                </span>
            ))}
        </div>
    );
}

function HolidayList({ items, onRemove, emptyLabel, testid }) {
    if (!items || items.length === 0) {
        return <p className="mt-4 text-sm text-[#475569]">{emptyLabel}</p>;
    }
    const lookup = Object.fromEntries(CA_HOLIDAYS.map((h) => [h.date, h.name]));
    const sorted = [...items].sort();
    return (
        <div className="mt-4 flex flex-wrap gap-2" data-testid={testid}>
            {sorted.map((v) => (
                <span
                    key={v}
                    className="inline-flex items-center gap-2 rounded-full border border-[#ECE4D3] bg-white px-3 py-1.5 text-xs font-medium text-[#0E2A47]"
                >
                    {v}
                    {lookup[v] && (
                        <span className="text-[#F26B1F]">· {lookup[v]}</span>
                    )}
                    <button
                        type="button"
                        onClick={() => onRemove(v)}
                        className="rounded-full p-0.5 text-rose-500 hover:bg-rose-50"
                        aria-label="Remove"
                    >
                        <Trash2 size={12} />
                    </button>
                </span>
            ))}
        </div>
    );
}
